// MODIFIED ChatSection.tsx - Added modal mode support
// frontend/src/components/dashboard/sections/ChatSection.tsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  ChatBubbleLeftRightIcon, 
  PaperAirplaneIcon,
  UserIcon,
  ComputerDesktopIcon,
  XMarkIcon,
  MinusIcon,
  ArrowsPointingOutIcon,
  ClockIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { chatBotService, ChatMessage, ChatSession, Employee } from '../../../services/chatbot.service';
import styles from './ChatSection.module.css';


  
interface ChatSectionProps {
  className?: string;
  isMinimized?: boolean;
  onToggleMinimize?: (minimized: boolean) => void;
  isModal?: boolean; // NEW: Indicates if used in modal context
}

const ChatSection: React.FC<ChatSectionProps> = ({ 
  className = '', 
  isMinimized = false,
  onToggleMinimize,
  isModal = false // NEW: Modal mode detection
}) => {
  // =====================
  // STATE MANAGEMENT
  // =====================
  const [session, setSession] = useState<ChatSession | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [error, setError] = useState<string | null>(null);
  
  // Refs for auto-scroll and input focus
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // =====================
  // INITIALIZATION
  // =====================
  useEffect(() => {
    initializeChatBot();
  }, []);

  const initializeChatBot = async () => {
    try {
      setConnectionStatus('connecting');
      
      // Test connection first
      const connectionTest = await chatBotService.testConnection();
      if (!connectionTest.success) {
        throw new Error(connectionTest.message);
      }
      
      // Initialize session
      const newSession = chatBotService.initializeSession();
      setSession(newSession);
      setConnectionStatus('connected');
      setError(null);
      
      console.log('🤖 ChatBot initialized successfully');
      
    } catch (error) {
      console.error('❌ ChatBot initialization failed:', error);
      setConnectionStatus('error');
      setError(error instanceof Error ? error.message : 'Failed to initialize chat');
    }
  };

  // =====================
  // MESSAGE HANDLING
  // =====================
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading || !session) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);
    setError(null);

    try {
      console.log('📤 Sending message:', messageText);
      
      const response = await chatBotService.processMessage(messageText);
      
      if (response.success) {
        // Update session with new messages
        const updatedSession = chatBotService.getCurrentSession();
        if (updatedSession) {
          setSession({ ...updatedSession });
        }
        console.log('✅ Message processed successfully');
      } else {
        setError(response.error || 'Failed to process message');
      }
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  }, [inputMessage, isLoading, session]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setInputMessage(suggestion);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  // =====================
  // AUTO-SCROLL EFFECT
  // =====================
  useEffect(() => {
    const scrollToBottom   = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end'
        });
      }
    };

    // Scroll when messages change
    if (session?.messages) {
      setTimeout(scrollToBottom, 100);
    }
  }, [session?.messages]);

  // =====================
  // MEMOIZED COMPONENTS
  // =====================
  const EmployeeCard = useMemo(() => ({ employee }: { employee: Employee }) => (
    <div className={styles.employeeCard}>
      <div className={styles.employeeCardHeader}>
        <div className={styles.employeeAvatar}>
          <img 
            src={employee.avatar || '/images/humanicon.png'} 
            alt={employee.name}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/images/humanicon.png';
            }}
          />
        </div>
        <div className={styles.employeeBasicInfo}>
          <h4 className={styles.employeeName}>{employee.name}</h4>
          <p className={styles.employeeId}>{employee.employeeNumber}</p>
          <p className={styles.employeeDesignation}>{employee.designation}</p>
        </div>
      </div>
      
      <div className={styles.employeeCardBody}>
        <div className={styles.employeeInfoGrid}>
          {employee.email && (
            <div className={styles.employeeInfoItem}>
              <EnvelopeIcon className={styles.infoIcon} />
              <span>{employee.email}</span>
            </div>
          )}
          {employee.phone && (
            <div className={styles.employeeInfoItem}>
              <PhoneIcon className={styles.infoIcon} />
              <span>{employee.phone}</span>
            </div>
          )}
          {employee.joinDate && (
            <div className={styles.employeeInfoItem}>
              <CalendarDaysIcon className={styles.infoIcon} />
              <span>Joined: {employee.joinDate}</span>
            </div>
          )}
          {employee.department && (
            <div className={styles.employeeInfoItem}>
              <BuildingOfficeIcon className={styles.infoIcon} />
              <span>{employee.department}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  ), []);

  const MessageBubble = useMemo(() => ({ message }: { message: ChatMessage }) => (
    <div className={`${styles.messageWrapper} ${styles[message.type]}`}>
      <div className={styles.messageHeader}>
        <div className={styles.messageAvatar}>
          {message.type === 'user' ? (
            <UserIcon className={styles.avatarIcon} />
          ) : (
            <ComputerDesktopIcon className={styles.avatarIcon} />
          )}
        </div>
        <div className={styles.messageMeta}>
          <span className={styles.messageSender}>
            {message.type === 'user' ? 'You' : 'PTS Assistant'}
          </span>
          <span className={styles.messageTime}>
            {message.timestamp.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>
      </div>
      
      <div className={styles.messageBubble}>
        <div className={styles.messageContent}>
          {message.content.split('\n').map((line, index) => (
            <div key={index} className={styles.messageLine}>
              {line.startsWith('•') ? (
                <span className={styles.bulletPoint}>{line}</span>
              ) : line.startsWith('#') ? (
                <h4 className={styles.messageHeading}>{line.substring(1).trim()}</h4>
              ) : (
                formatMessageText(line)
              )}
            </div>
          ))}
        </div>
        
        {/* Employee Card Integration */}
        {message.employeeData && (
          <EmployeeCard employee={message.employeeData} />
        )}
        
        {/* Suggestions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className={styles.suggestions}>
            <p className={styles.suggestionsLabel}>💡 Quick Actions:</p>
            <div className={styles.suggestionsList}>
              {message.suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className={styles.suggestionButton}
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={isLoading}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  ), [handleSuggestionClick, isLoading, EmployeeCard]);

  // =====================
  // HELPER FUNCTIONS
  // =====================
  const formatMessageText = (text: string) => {
    // Handle bold text
    const boldRegex = /\*\*(.*?)\*\*/g;
    const parts = text.split(boldRegex);
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return <strong key={index} className={styles.boldText}>{part}</strong>;
      }
      return part;
    });
  };

  const handleToggleMinimize = () => {
    if (onToggleMinimize) {
      onToggleMinimize(!isMinimized);
    }
  };

  // =====================
  // LOADING STATE
  // =====================
  if (connectionStatus === 'connecting') {
    return (
      <div className={`${styles.chatSection} ${className}`}>
        <div className={styles.chatHeader}>
          <div className={styles.headerInfo}>
            <ChatBubbleLeftRightIcon className={styles.headerIcon} />
            <div>
              <h3 className={styles.headerTitle}>PTS Assistant</h3>
              <p className={styles.headerSubtitle}>Connecting...</p>
            </div>
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Initializing AI Assistant...</p>
        </div>
      </div>
    );
  }

  // =====================
  // ERROR STATE
  // =====================
  if (connectionStatus === 'error') {
    return (
      <div className={`${styles.chatSection} ${className}`}>
        <div className={styles.chatHeader}>
          <div className={styles.headerInfo}>
            <ChatBubbleLeftRightIcon className={styles.headerIcon} />
            <div>
              <h3 className={styles.headerTitle}>PTS Assistant</h3>
              <p className={styles.headerSubtitle}>Connection Error</p>
            </div>
          </div>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>❌</div>
          <h4 className={styles.errorTitle}>Connection Failed</h4>
          <p className={styles.errorMessage}>{error}</p>
          <button className={styles.retryButton} onClick={initializeChatBot}>
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // =====================
  // MAIN RENDER
  // =====================
  return (
    <div className={`${styles.chatSection} ${isMinimized ? styles.minimized : ''} ${className}`}>
      {/* Chat Header */}
      <div className={styles.chatHeader}>
        <div className={styles.headerInfo}>
          <ChatBubbleLeftRightIcon className={styles.headerIcon} />
          <div>
            <h3 className={styles.headerTitle}>PTS Assistant</h3>
            <p className={styles.headerSubtitle}>
              {session?.context.sessionStats.employeesQueried || 0} employees queried
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          {!isModal && ( // NEW: Only show minimize button when not in modal
            <button 
              className={styles.headerButton}
              onClick={handleToggleMinimize}
              title={isMinimized ? "Expand chat" : "Minimize chat"}
            >
              {isMinimized ? <ArrowsPointingOutIcon /> : <MinusIcon />}
            </button>
          )}
        </div>
      </div>

      {/* Chat Body */}
      {!isMinimized && (
        <>
          <div className={styles.chatBody} ref={messagesContainerRef}>
            <div className={styles.messagesContainer}>
              {session?.messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className={`${styles.messageWrapper} ${styles.assistant}`}>
                  <div className={styles.messageHeader}>
                    <div className={styles.messageAvatar}>
                      <ComputerDesktopIcon className={styles.avatarIcon} />
                    </div>
                    <div className={styles.messageMeta}>
                      <span className={styles.messageSender}>PTS Assistant</span>
                      <span className={styles.messageTime}>typing...</span>
                    </div>
                  </div>
                  <div className={styles.messageBubble}>
                    <div className={styles.typingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Chat Input */}
          <div className={styles.chatInput}>
            {error && (
              <div className={styles.inputError}>
                <span className={styles.errorText}>❌ {error}</span>
                <button 
                  className={styles.dismissError}
                  onClick={() => setError(null)}
                >
                  <XMarkIcon />
                </button>
              </div>
            )}
            
            <div className={styles.inputContainer}>
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about any employee... (e.g., 'Show me employee 1001')"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className={styles.messageInput}
                autoComplete="off"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className={styles.sendButton}
                title="Send message"
              >
                <PaperAirplaneIcon className={styles.sendIcon} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(ChatSection);